import apiClient, { setAuthTokenGetter } from '@/api/client';
import { User } from '@/api/models';
import { AccessDenied } from '@/components/auth/AccessDenied';
import { AccountDisabled } from '@/components/auth/AccountDisabled';
import { useAccessToken, useAuth } from '@/lib/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { queryKeys } from './queryKeys';

export interface AppContextType {
  currentUser: User | null;
  isLoggedIn: boolean | null;
  logOut: () => void;
}

const INITIAL_STATE: AppContextType = {
  currentUser: null,
  isLoggedIn: null,
  logOut: () => {},
};

const AppContext = React.createContext<AppContextType>(INITIAL_STATE);

export const useApp = () => React.useContext(AppContext);

export const AppProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { user, isLoading: isAuthLoading, logout } = useAuth();
  const queryClient = useQueryClient();
  const getToken = useAccessToken();

  // Set up API client auth token getter
  React.useEffect(() => {
    setAuthTokenGetter(getToken);
  }, [getToken]);

  // Fetch current user data when authenticated
  const { data: currentUser, isLoading: isUserLoading } = useQuery({
    queryKey: [queryKeys.ADMIN_USER, user?.sub],
    queryFn: async () => {
      const { data } = await apiClient.GET('/admin/users/me');
      return data;
    },
    enabled: !!user,
  });

  // Compute login state
  const isLoggedIn = React.useMemo(() => {
    if (isAuthLoading || isUserLoading) {
      return null; // Still loading
    }
    return !!user && !!currentUser;
  }, [isAuthLoading, isUserLoading, user, currentUser]);

  // Check if user has any roles (no roles = regular user, not authorized for admin)
  const hasAdminAccess = React.useMemo(() => {
    if (!currentUser) return null; // Still loading or not logged in
    return currentUser.roles && currentUser.roles.length > 0;
  }, [currentUser]);

  // Check if user account is disabled
  const isAccountDisabled = React.useMemo(() => {
    if (!currentUser) return null;
    return currentUser.enabled === false;
  }, [currentUser]);

  const handleLogOut = async () => {
    try {
      queryClient.clear();
      await logout({ logoutParams: { returnTo: window.location.origin } });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value: AppContextType = {
    currentUser: currentUser ?? null,
    isLoggedIn,
    logOut: handleLogOut,
  };

  // Block access for disabled users
  if (isLoggedIn && isAccountDisabled) {
    return <AccountDisabled onLogOut={handleLogOut} />;
  }

  // Block access for users without roles
  if (isLoggedIn && hasAdminAccess === false) {
    return <AccessDenied onLogOut={handleLogOut} />;
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
